import { ref, computed, onUnmounted, unref } from 'vue';

// Below this speed (px/ms) the release is considered stationary and the
// position fallback decides; above it, the drag direction decides.
const INTENT_VELOCITY_PX_MS = 0.1;
// Stationary release: dismiss only when dragged past this ratio of the sheet.
const STATIONARY_DISMISS_RATIO = 0.4;
// Only samples within this window count towards the release velocity, so a
// jitter at lift-off doesn't flip the perceived direction.
const VELOCITY_WINDOW_MS = 100;
const MAX_MOVE_SAMPLES = 10;

// Exit physics: the sheet keeps the release velocity and accelerates away.
const EXIT_ACCELERATION_PX_MS2 = 0.005;
const MIN_EXIT_VELOCITY_PX_MS = 0.4;
// Return physics: critically damped spring pulling the sheet back to open.
const SPRING_OMEGA_PER_MS = 0.02;
const SETTLE_DISTANCE_PX = 0.5;

export function useDrawerDrag({ sheetRef, enabled, onDismiss }) {
  const dragOffset = ref(0);
  const isDragging = ref(false);
  const measuredSheetHeight = ref(0);

  let activePointerId = null;
  let startY = 0;
  let startOffset = 0;
  let lastMoveSamples = [];
  let animationFrameId = null;

  const dragProgress = computed(() => {
    if (measuredSheetHeight.value <= 0) return 0;
    return Math.min(dragOffset.value / measuredSheetHeight.value, 1);
  });

  const cancelAnimation = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  };

  const removeDocumentListeners = () => {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerCancel);
  };

  const resetDragState = () => {
    isDragging.value = false;
    lastMoveSamples = [];
  };

  const resetDrag = () => {
    cancelAnimation();
    activePointerId = null;
    removeDocumentListeners();
    resetDragState();
    dragOffset.value = 0;
    measuredSheetHeight.value = 0;
  };

  // Velocity (px/ms, positive = downward) measured over the recent window.
  const getReleaseVelocity = () => {
    if (lastMoveSamples.length < 2) return 0;

    const newest = lastMoveSamples[lastMoveSamples.length - 1];
    let oldest = lastMoveSamples[0];
    for (const sample of lastMoveSamples) {
      if (newest.t - sample.t <= VELOCITY_WINDOW_MS) {
        oldest = sample;
        break;
      }
    }

    const dt = newest.t - oldest.t;
    if (dt <= 0) return 0;
    return (newest.y - oldest.y) / dt;
  };

  // Ballistic exit: continues at the release velocity and accelerates
  // downward until the sheet is fully off-screen.
  const animateExit = (releaseVelocity, onComplete) => {
    cancelAnimation();
    const x0 = dragOffset.value;
    const v0 = Math.max(releaseVelocity, MIN_EXIT_VELOCITY_PX_MS);
    const target = measuredSheetHeight.value;
    const startTime = performance.now();

    const step = (now) => {
      const t = now - startTime;
      const x = x0 + v0 * t + 0.5 * EXIT_ACCELERATION_PX_MS2 * t * t;

      if (x >= target) {
        animationFrameId = null;
        dragOffset.value = target;
        onComplete?.();
        return;
      }

      dragOffset.value = x;
      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);
  };

  // Critically damped spring back to the open position, seeded with the
  // release velocity so an upward fling visibly carries through.
  const animateReturn = (releaseVelocity, onComplete) => {
    cancelAnimation();
    const x0 = dragOffset.value;
    const v0 = releaseVelocity;
    const omega = SPRING_OMEGA_PER_MS;
    const startTime = performance.now();

    const step = (now) => {
      const t = now - startTime;
      const decay = Math.exp(-omega * t);
      const x = (x0 + (v0 + omega * x0) * t) * decay;

      if (x <= SETTLE_DISTANCE_PX) {
        animationFrameId = null;
        dragOffset.value = 0;
        onComplete?.();
        return;
      }

      dragOffset.value = x;
      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);
  };

  const snapBack = (releaseVelocity = 0) => {
    animateReturn(releaseVelocity, resetDragState);
  };

  const dismiss = (releaseVelocity = 0) => {
    animateExit(releaseVelocity, () => {
      resetDragState();
      onDismiss();
    });
  };

  const finishGesture = (shouldDismiss, releaseVelocity) => {
    removeDocumentListeners();
    activePointerId = null;

    if (shouldDismiss) {
      dismiss(releaseVelocity);
    } else {
      snapBack(releaseVelocity);
    }
  };

  const onPointerMove = (event) => {
    if (activePointerId === null || event.pointerId !== activePointerId) return;

    const delta = event.clientY - startY;
    dragOffset.value = Math.max(0, startOffset + delta);

    lastMoveSamples.push({ y: event.clientY, t: performance.now() });
    if (lastMoveSamples.length > MAX_MOVE_SAMPLES) {
      lastMoveSamples.shift();
    }
  };

  const onPointerUp = (event) => {
    if (activePointerId === null || event.pointerId !== activePointerId) return;

    const velocity = getReleaseVelocity();

    // Direction of movement at release wins; position only decides when the
    // finger was effectively stationary.
    let shouldDismiss;
    if (velocity > INTENT_VELOCITY_PX_MS) {
      shouldDismiss = true;
    } else if (velocity < -INTENT_VELOCITY_PX_MS) {
      shouldDismiss = false;
    } else {
      shouldDismiss = dragProgress.value > STATIONARY_DISMISS_RATIO;
    }

    finishGesture(shouldDismiss, velocity);
  };

  const onPointerCancel = (event) => {
    if (activePointerId === null || event.pointerId !== activePointerId) return;
    removeDocumentListeners();
    activePointerId = null;
    snapBack();
  };

  const onHandlePointerDown = (event) => {
    if (!unref(enabled) || event.button !== 0) return;

    const sheet = sheetRef.value;
    if (!sheet) return;

    event.preventDefault();
    cancelAnimation();

    measuredSheetHeight.value = sheet.getBoundingClientRect().height;
    startY = event.clientY;
    startOffset = dragOffset.value;
    activePointerId = event.pointerId;
    isDragging.value = true;
    lastMoveSamples = [{ y: event.clientY, t: performance.now() }];

    document.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('pointerup', onPointerUp, { passive: true });
    document.addEventListener('pointercancel', onPointerCancel, {
      passive: true,
    });
  };

  onUnmounted(() => {
    resetDrag();
  });

  return {
    dragOffset,
    isDragging,
    dragProgress,
    onHandlePointerDown,
    resetDrag,
  };
}
